import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Tag, ChevronRight, Trophy, TrendingDown, Flame } from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { ROUTE_PATHS, Store } from '@/lib/index';
import MapleLeaf from '@/components/MapleLeaf';
import { staggerContainer, staggerItem, springPresets } from '@/lib/motion';

// Rough savings estimate based on deal volume
function estSavings(dealCount: number): string {
  const low  = Math.round(dealCount * 0.40);
  const high = Math.round(dealCount * 0.85);
  return `~$${low}–$${high}`;
}

export default function StoresPage() {
  const { stores, postalCode, setStep, selectStore } = useAppStore();
  const navigate = useNavigate();

  if (!stores.length) { navigate(ROUTE_PATHS.HOME); return null; }

  // Sort by deal count descending so best-value store is always first
  const sorted = [...stores].sort((a, b) => (b.dealCount ?? 0) - (a.dealCount ?? 0));
  const topIdx  = 0; // sorted[0] is always best

  const handlePick = (store: Store) => {
    selectStore(store);
    setStep('scanning');
    navigate(`${ROUTE_PATHS.LOADING}?postal=${encodeURIComponent(postalCode)}&merchant=${encodeURIComponent(store.name)}`);
  };

  return (
    <div className="flex flex-col bg-background overflow-hidden" style={{ paddingTop: '48px', height: '100dvh' }}>
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(ROUTE_PATHS.HOME)}
            className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <MapleLeaf className="w-4 h-4" />
            <span className="font-black text-foreground tracking-tight text-base uppercase">Pick Your Store</span>
          </div>
          <span className="text-xs text-muted-foreground font-mono">{postalCode}</span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto overscroll-contain">
      <div className="container mx-auto max-w-lg px-4 py-6 pb-10">
        <p className="text-sm text-muted-foreground mb-4">
          Found <strong className="text-foreground">{sorted.length} stores</strong> near {postalCode} —
          sorted by most deals this week.
        </p>

        <motion.ul variants={staggerContainer} initial="hidden" animate="visible" className="space-y-3">
          {sorted.map((store, i) => {
            const isTop    = i === topIdx && (store.dealCount ?? 0) > 0;
            const isHot    = !isTop && i === 1 && (store.dealCount ?? 0) >= 40;
            const dealCnt  = store.dealCount ?? 0;

            return (
              <motion.li key={store.name} variants={staggerItem}>
                <button
                  onClick={() => handlePick(store)}
                  className={`w-full text-left rounded-2xl p-4 transition-all group active:scale-[0.99]
                    ${isTop
                      ? 'bg-primary/5 border-2 border-primary/40 hover:border-primary/70 hover:shadow-md shadow-sm shadow-primary/10'
                      : 'bg-card border border-border hover:border-primary/50 hover:shadow-md'
                    }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Store emoji badge */}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 shadow-sm"
                        style={{ backgroundColor: store.color ?? '#16a34a' }}>
                        {store.emoji ?? '🏪'}
                      </div>

                      <div className="min-w-0 flex-1">
                        {/* Name row */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-foreground text-base leading-tight">{store.name}</span>
                          {isTop && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-black bg-amber-400 text-amber-950 px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
                              <Trophy className="w-2.5 h-2.5" /> Best Value
                            </span>
                          )}
                          {isHot && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-black bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
                              <Flame className="w-2.5 h-2.5" /> Hot Deals
                            </span>
                          )}
                        </div>

                        {/* Deal count + savings row */}
                        {dealCnt > 0 ? (
                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              <Tag className="w-2.5 h-2.5" /> {dealCnt} deals
                            </span>
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              <TrendingDown className="w-2.5 h-2.5" /> Est. {estSavings(dealCnt)} savings
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground mt-1 block">No deals found this week</span>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                  </div>
                </button>
              </motion.li>
            );
          })}
        </motion.ul>

        <p className="text-center text-[10px] text-muted-foreground mt-6 opacity-60">
          Flyer data from Flipp.com · Updated weekly · Savings estimates based on deal volume
        </p>
      </div>
      </div>{/* end scrollable */}
    </div>
  );
}
